// src/store/crud.ts

import type { NestedPartial } from "@directus/sdk";
import type { VirtualDeviceConfiguration, VirtualDeviceConfigurationRecord } from "@irriga-mais/common";
import { atom } from "jotai";
import { apiService } from "@/api";
import type { Device } from "@/api/model/device";
import type { DeviceMessageRequest } from "@/api/model/device-message-request";
import type { IrrigationPlan } from "@/api/model/irrigation-plan";
import type { IrrigationPlanSectorMirror } from "@/api/model/irrigation-plan-sector-mirror";
import type { IrrigationPlanStep } from "@/api/model/irrigation-plan-step";
import type { IrrigationPlanStepTank } from "@/api/model/irrigation-plan-step-tank";
import type { MeshDeviceMapping } from "@/api/model/mesh-device-mapping";
import type { Project } from "@/api/model/project";
import type { ProjectTank } from "@/api/model/project-tank";
import type { Property } from "@/api/model/property";
import type { PropertyDevice } from "@/api/model/property-device";
import type { PropertyDeviceSensorAssignment } from "@/api/model/property-device-sensor-assignment";
import type { Reservoir } from "@/api/model/reservoir";
import type { Sector } from "@/api/model/sector";
import type { SectorCrop } from "@/api/model/sector-crop";
import type { Tank } from "@/api/model/tank";
import type { WaterPump } from "@/api/model/water-pump";
import { refetchDataAtom } from "./data";
import { type OperationResult, withOperationHandlingAtom } from "./operations";

/**
 * Generic operation factory that can handle any arguments
 * @param operation - The async operation function to execute
 * @param operationType - The type of operation for tracking
 * @param operationMessage - Optional message to display during operation
 * @param options - Optional configuration
 * @returns A write-only atom that executes the operation
 */
const createGenericOperation = <TArgs extends any[], TResult>(
  operation: (...args: TArgs) => Promise<TResult>,
  operationType: string,
  operationMessage?: string,
  options?: {
    displayLoading?: boolean;
    displayError?: boolean;
    refetchData?: boolean;
  }
) => {
  const { displayLoading = true, displayError = true, refetchData = true } = options || {};

  return atom(null, async (get, set, ...args: TArgs) => {
    const result = await set(
      withOperationHandlingAtom,
      {
        operation: operationType,
        message: operationMessage || `${operationType}...`,
        displayLoading,
        displayError,
      },
      async () => {
        const apiResult = await operation(...args);
        // Refetch the account user tree after successful operation if enabled
        if (refetchData) {
          set(refetchDataAtom);
        }
        return apiResult;
      }
    );

    return result;
  });
};

const createCreateOperation = <T>(
  operation: (data: Partial<T>) => Promise<T>,
  operationType: string,
  operationMessage?: string
) => {
  return atom(null, async (get, set, data: Partial<T>) => {
    const result = await set(
      withOperationHandlingAtom,
      {
        operation: operationType,
        message: operationMessage || `${operationType}...`,
        displayLoading: true,
        displayError: true,
      },
      async () => {
        const apiResult = await operation(data);
        // Refetch the account user tree after successful operation
        set(refetchDataAtom);
        return apiResult;
      }
    );

    return result as OperationResult<T>;
  });
};

const createCreateBatchOperation = <T>(
  operation: (data: Partial<T>[]) => Promise<T[]>,
  operationType: string,
  operationMessage?: string
) => {
  return atom(null, async (get, set, data: Partial<T>[]) => {
    const result = await set(
      withOperationHandlingAtom,
      {
        operation: operationType,
        message: operationMessage || `${operationType}...`,
        displayLoading: true,
        displayError: true,
      },
      async () => {
        const apiResult = await operation(data);
        // Refetch the account user tree after successful operation
        set(refetchDataAtom);
        return apiResult;
      }
    );

    return result as OperationResult<T[]>;
  });
};

const createUpdateOperation = <T>(
  operation: (id: string, data: Partial<T>) => Promise<T>,
  operationType: string,
  operationMessage?: string
) => {
  return atom(null, async (get, set, { id, data }: { id: string; data: Partial<T> }) => {
    const result = await set(
      withOperationHandlingAtom,
      {
        operation: operationType,
        message: operationMessage || `${operationType}...`,
        displayLoading: true,
        displayError: true,
      },
      async () => {
        const apiResult = await operation(id, data);
        // Refetch the account user tree after successful operation
        set(refetchDataAtom);
        return apiResult;
      }
    );

    return result;
  });
};

const createUpdateBatchOperation = <T>(
  operation: (items: Partial<T>[]) => Promise<T[]>,
  operationType: string,
  operationMessage?: string
) => {
  return atom(null, async (get, set, items: Partial<T>[]) => {
    const result = await set(
      withOperationHandlingAtom,
      {
        operation: operationType,
        message: operationMessage || `${operationType}...`,
        displayLoading: true,
        displayError: true,
      },
      async () => {
        const apiResult = await operation(items);
        // Refetch the account user tree after successful operation
        set(refetchDataAtom);
        return apiResult;
      }
    );

    return result;
  });
};

const createDeleteOperation = <T>(
  operation: (id: string) => Promise<void>,
  operationType: string,
  operationMessage?: string
) => {
  return atom(null, async (get, set, id: string) => {
    const result = await set(
      withOperationHandlingAtom,
      {
        operation: operationType,
        message: operationMessage || `${operationType}...`,
        displayLoading: true,
        displayError: true,
      },
      async () => {
        await operation(id);
        // Refetch the account user tree after successful operation
        console.log("Refetching data after delete");
        set(refetchDataAtom);
        return { success: true };
      }
    );
    return result;
  });
};

// Property operations
export const createPropertyAtom = createCreateOperation(
  (data: Partial<Property>) => apiService.property.create(data),
  "createProperty",
  "Criando propriedade..."
);

export const updatePropertyAtom = createUpdateOperation(
  (id: string, data: Partial<Property>) => apiService.property.update(id, data),
  "updateProperty",
  "Atualizando propriedade..."
);

// Project operations
export const createProjectAtom = createCreateOperation(
  (data: Partial<Project>) => apiService.project.create(data),
  "createProject",
  "Criando projeto..."
);

export const updateProjectAtom = createUpdateOperation(
  (id: string, data: Partial<Project>) => apiService.project.update(id, data),
  "updateProject",
  "Atualizando projeto..."
);

// Sector operations
export const createSectorAtom = createCreateOperation(
  (data: Partial<Sector>) => apiService.sector.create(data),
  "createSector",
  "Criando setor..."
);

export const updateSectorAtom = createUpdateOperation(
  (id: string, data: Partial<Sector>) => apiService.sector.update(id, data),
  "updateSector",
  "Atualizando setor..."
);

export const deleteSectorAtom = createDeleteOperation(
  (id: string) => apiService.sector.del(id),
  "deleteSector",
  "Excluindo setor..."
);

// Water Pump operations
export const createWaterPumpAtom = createCreateOperation(
  (data: Partial<WaterPump>) => apiService.waterPump.create(data),
  "createWaterPump",
  "Criando bomba de água..."
);

export const updateWaterPumpAtom = createUpdateOperation(
  (id: string, data: Partial<WaterPump>) => apiService.waterPump.update(id, data),
  "updateWaterPump",
  "Atualizando bomba de água..."
);

export const deleteWaterPumpAtom = createDeleteOperation(
  (id: string) => apiService.waterPump.del(id),
  "deleteWaterPump",
  "Excluindo bomba de água..."
);

// Reservoir operations
export const createReservoirAtom = createCreateOperation(
  (data: Partial<Reservoir>) => apiService.reservoir.create(data),
  "createReservoir",
  "Criando reservatório..."
);

export const updateReservoirAtom = createUpdateOperation(
  (id: string, data: Partial<Reservoir>) => apiService.reservoir.update(id, data),
  "updateReservoir",
  "Atualizando reservatório..."
);

export const deleteReservoirAtom = createDeleteOperation(
  (id: string) => apiService.reservoir.del(id),
  "deleteReservoir",
  "Excluindo reservatório..."
);

// Tank operations
export const createTankAtom = createCreateOperation(
  (data: Partial<Tank>) => apiService.tank.create(data),
  "createTank",
  "Criando tanque..."
);

export const updateTankAtom = createUpdateOperation(
  (id: string, data: Partial<Tank>) => apiService.tank.update(id, data),
  "updateTank",
  "Atualizando tanque..."
);

export const deleteTankAtom = createDeleteOperation(
  (id: string) => apiService.tank.del(id),
  "deleteTank",
  "Excluindo tanque..."
);

// Sector Crop operations
export const createSectorCropAtom = createCreateOperation(
  (data: Partial<SectorCrop>) => apiService.sectorCrop.create(data),
  "createSectorCrop",
  "Criando cultivo do setor..."
);

export const updateSectorCropAtom = createUpdateOperation(
  (id: string, data: Partial<SectorCrop>) => apiService.sectorCrop.update(id, data),
  "updateSectorCrop",
  "Atualizando cultivo do setor..."
);

export const deleteSectorCropAtom = createDeleteOperation(
  (id: string) => apiService.sectorCrop.del(id),
  "deleteSectorCrop",
  "Excluindo cultivo do setor..."
);

// Project Tank operations
export const createProjectTankAtom = createCreateOperation(
  (data: Partial<ProjectTank>) => apiService.projectTank.create(data),
  "createProjectTank",
  "Atribuindo tanque ao projeto..."
);

export const updateProjectTankAtom = createUpdateOperation(
  (id: string, data: Partial<ProjectTank>) => apiService.projectTank.update(id, data),
  "updateProjectTank",
  "Atualizando atribuição de tanque..."
);

export const deleteProjectTankAtom = createDeleteOperation(
  (id: string) => apiService.projectTank.del(id),
  "deleteProjectTank",
  "Removendo tanque do projeto..."
);

// Irrigation Plan Step Tank operations
export const createIrrigationPlanStepTankAtom = createCreateOperation(
  (data: Partial<IrrigationPlanStepTank>) => apiService.irrigationPlanStepTank.create(data),
  "createIrrigationPlanStepTank",
  "Adicionando tanque à etapa..."
);

export const deleteIrrigationPlanStepTankAtom = createDeleteOperation(
  (id: string) => apiService.irrigationPlanStepTank.del(id),
  "deleteIrrigationPlanStepTank",
  "Removendo tanque da etapa..."
);

// Irrigation Plan operations
export const createIrrigationPlanAtom = createCreateOperation(
  (data: NestedPartial<IrrigationPlan>) => apiService.irrigationPlan.create(data),
  "createIrrigationPlan",
  "Criando plano de irrigação..."
);

export const updateIrrigationPlanAtom = createUpdateOperation(
  (id: string, data: Partial<IrrigationPlan>) => apiService.irrigationPlan.update(id, data),
  "updateIrrigationPlan",
  "Atualizando plano de irrigação..."
);

export const deleteIrrigationPlanAtom = createDeleteOperation(
  (id: string) => apiService.irrigationPlan.del(id),
  "deleteIrrigationPlan",
  "Apagando plano de irrigação..."
);

// Irrigation Plan Step operations
export const createIrrigationPlanStepAtom = createCreateOperation(
  (data: Partial<IrrigationPlanStep>) => apiService.irrigationPlanStep.create(data),
  "createIrrigationPlanStep",
  "Criando etapa do plano..."
);

export const updateIrrigationPlanStepAtom = createUpdateOperation(
  (id: string, data: Partial<IrrigationPlanStep>) => apiService.irrigationPlanStep.update(id, data),
  "updateIrrigationPlanStep",
  "Atualizando etapa do plano..."
);

export const swapIrrigationPlanStepsOrdersAtom = createGenericOperation(
  (step1: Pick<IrrigationPlanStep, "id" | "order">, step2: Pick<IrrigationPlanStep, "id" | "order">) =>
    apiService.irrigationPlanStep.swapOrders(step1, step2),
  "swapIrrigationPlanStep",
  "Trocando ordem das etapas..."
);

export const updateIrrigationPlanStepBatchAtom = createUpdateBatchOperation(
  (items: Partial<IrrigationPlanStep>[]) => apiService.irrigationPlanStep.updateBatch(items),
  "updateIrrigationPlanStepBatch",
  "Atualizando etapas do plano..."
);

export const deleteIrrigationPlanStepAtom = createDeleteOperation(
  (id: string) => apiService.irrigationPlanStep.del(id),
  "deleteIrrigationPlanStep",
  "Excluindo etapa do plano..."
);

// Irrigation Plan Sector Mirror operations
export const createIrrigationPlanSectorMirrorAtom = createCreateOperation(
  (data: Partial<IrrigationPlanSectorMirror>) => apiService.irrigationPlanSectorMirror.create(data),
  "createIrrigationPlanSectorMirror",
  "Criando espelho de setor..."
);

export const deleteIrrigationPlanSectorMirrorAtom = createDeleteOperation(
  (id: string) => apiService.irrigationPlanSectorMirror.del(id),
  "deleteIrrigationPlanSectorMirror",
  "Excluindo espelho de setor..."
);

// Property Device operations
export const createPropertyDeviceAtom = createCreateOperation(
  (data: Partial<PropertyDevice>) => apiService.propertyDevice.create(data),
  "createPropertyDevice",
  "Criando dispositivo..."
);

export const updatePropertyDeviceAtom = createUpdateOperation(
  (id: string, data: Partial<PropertyDevice>) => apiService.propertyDevice.update(id, data),
  "updatePropertyDevice",
  "Atualizando dispositivo..."
);

// Property Device Sensor Assignment operations
export const createPropertyDeviceSensorAssignmentAtom = createCreateOperation(
  (data: Partial<PropertyDeviceSensorAssignment>) => apiService.propertyDeviceSensorAssignment.create(data),
  "createPropertyDeviceSensorAssignment",
  "Atribuindo sensor..."
);

export const updatePropertyDeviceSensorAssignmentAtom = createUpdateOperation(
  (id: string, data: Partial<PropertyDeviceSensorAssignment>) =>
    apiService.propertyDeviceSensorAssignment.update(id, data),
  "updatePropertyDeviceSensorAssignment",
  "Atualizando atribuição de sensor..."
);

export const deletePropertyDeviceSensorAssignmentAtom = createDeleteOperation(
  (id: string) => apiService.propertyDeviceSensorAssignment.del(id),
  "deletePropertyDeviceSensorAssignment",
  "Removendo sensor..."
);

export const updateDeviceAtom = createUpdateOperation(
  (id: string, data: Partial<Device>) => apiService.device.update(id, data),
  "updateDevice",
  "Atualizando dispositivo..."
);

// Mesh Device Mapping operations
export const createMeshDeviceMappingAtom = createCreateOperation(
  (data: Partial<MeshDeviceMapping>) => apiService.meshDeviceMapping.create(data),
  "createMeshDeviceMapping",
  "Criando mapeamento de dispositivo..."
);

export const createMeshDeviceMappingBatchAtom = createCreateBatchOperation(
  (data: Partial<MeshDeviceMapping>[]) => apiService.meshDeviceMapping.createBatch(data),
  "createMeshDeviceMappingBatch",
  "Criando mapeamento de dispositivos..."
);

export const updateMeshDeviceMappingAtom = createUpdateOperation(
  (id: string, data: Partial<MeshDeviceMapping>) => apiService.meshDeviceMapping.update(id, data),
  "updateMeshDeviceMapping",
  "Atualizando mapeamento de dispositivo..."
);

export const updateMeshDeviceMappingBatchAtom = createUpdateBatchOperation(
  (items: Partial<MeshDeviceMapping>[]) => apiService.meshDeviceMapping.updateBatch(items),
  "updateMeshDeviceMappingBatch",
  "Atualizando mapeamento de dispositivos..."
);

export const deleteMeshDeviceMappingAtom = createDeleteOperation(
  (id: string) => apiService.meshDeviceMapping.del(id),
  "deleteMeshDeviceMapping",
  "Removendo mapeamento de dispositivo..."
);

// Device Message Request operations
export const createDeviceMessageRequestAtom = createCreateOperation(
  (data: Partial<DeviceMessageRequest>) => apiService.deviceMessageRequest.create(data),
  "createDeviceMessageRequest",
  "Criando solicitação de mensagem..."
);

export const updateDeviceMessageRequestAtom = createUpdateOperation(
  (id: string, data: Partial<DeviceMessageRequest>) => apiService.deviceMessageRequest.update(id, data),
  "updateDeviceMessageRequest",
  "Atualizando solicitação de mensagem..."
);

export const deleteDeviceMessageRequestAtom = createDeleteOperation(
  (id: string) => apiService.deviceMessageRequest.del(id),
  "deleteDeviceMessageRequest",
  "Excluindo solicitação de mensagem..."
);

// Virtual Device Configuration operations
export const createVirtualDeviceConfigurationAtom = createCreateOperation(
  (data: Partial<VirtualDeviceConfiguration> | Partial<VirtualDeviceConfigurationRecord>) =>
    apiService.virtualDeviceConfiguration.create(data as any),
  "createVirtualDeviceConfiguration",
  "Criando configuração de dispositivo..."
);

export const updateVirtualDeviceConfigurationAtom = createUpdateOperation(
  (id: string, data: Partial<VirtualDeviceConfiguration> | Partial<VirtualDeviceConfigurationRecord>) =>
    apiService.virtualDeviceConfiguration.update(id, data as any),
  "updateVirtualDeviceConfiguration",
  "Atualizando configuração de dispositivo..."
);

export const deleteVirtualDeviceConfigurationAtom = createDeleteOperation(
  (id: string) => apiService.virtualDeviceConfiguration.del(id),
  "deleteVirtualDeviceConfiguration",
  "Excluindo configuração de dispositivo..."
);

// Export the generic operation factory for use in other files
export { createGenericOperation };
